import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import sideNavTranslations from 'i2l?query=sporks!../../lang/sideNav/en.lyaml';
import SharedSideNav from 'pricing-pages-ui-components/components/layout/side-nav/SideNav';
import { useScopedTranslations } from 'pricing-pages-ui-components/i18n/useScopedTranslations';
import { useGetPublicProductGroupsLazyQuery } from 'pricing-pages-ui-public-app/__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc.hooks';
import { useLocation } from 'quartz-routing/react-router';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { DEFAULT_CHIRP_TIMEOUT } from '../constants/chirpDefaults';
import { useCurrency } from '../context/CurrencyContext';
import { PublicI18nContext } from '../context/I18nContext';
import { useIsRevenueHubEnabled } from '../hooks/useIsRevenueHubEnabled';
import { isCountryCodeString } from '../utils/currencyTypes';
import { getBrowserLocale, getCountryCode } from '../utils/getPreferredLanguage';
import { getProductGroupBySlug } from '../utils/getProductGroupBySlug';
import { shouldSkipSpeculativePrefetch } from '../utils/speculativePrefetch';
import PublicCurrencyPicker from './PublicCurrencyPicker';
import { jsx as _jsx } from "react/jsx-runtime";
const HOVER_INTENT_DELAY_MS = 100;
const PublicSideNav = ({
  selectedNavItem
}) => {
  var _sections$flatMap$fin, _sections$flatMap$fin2;
  const {
    search
  } = useLocation();
  const {
    i18nLoader
  } = useContext(PublicI18nContext);
  const isRevenueHubEnabled = useIsRevenueHubEnabled();
  const {
    isReady,
    Skeleton
  } = useScopedTranslations(sideNavTranslations, i18nLoader);
  const {
    displayedCurrencyCode
  } = useCurrency();
  const [fetchProductGroup] = useGetPublicProductGroupsLazyQuery();
  const prefetchedKeys = useRef(new Set());
  const hoverIntentTimer = useRef(null);
  const prefetchProductGroupData = useCallback(navItem => {
    const productGroup = getProductGroupBySlug(navItem);
    if (!productGroup) return;
    const key = `${String(productGroup)}:${displayedCurrencyCode}`;
    if (prefetchedKeys.current.has(key)) return;
    prefetchedKeys.current.add(key);
    const rawCountry = getCountryCode();
    void fetchProductGroup({
      variables: {
        productGroup,
        currencyCode: displayedCurrencyCode,
        preferredCountry: isCountryCodeString(rawCountry) ? rawCountry : undefined,
        browserLocale: getBrowserLocale()
      },
      context: {
        timeout: DEFAULT_CHIRP_TIMEOUT
      }
    });
  }, [displayedCurrencyCode, fetchProductGroup]);
  const cancelHoverIntent = useCallback(() => {
    if (hoverIntentTimer.current !== null) {
      clearTimeout(hoverIntentTimer.current);
      hoverIntentTimer.current = null;
    }
  }, []);
  const scheduleHoverIntent = useCallback(navItem => {
    if (shouldSkipSpeculativePrefetch()) return;
    cancelHoverIntent();
    hoverIntentTimer.current = setTimeout(() => {
      hoverIntentTimer.current = null;
      prefetchProductGroupData(navItem);
    }, HOVER_INTENT_DELAY_MS);
  }, [cancelHoverIntent, prefetchProductGroupData]);
  useEffect(() => cancelHoverIntent, [cancelHoverIntent]);

  // Guard unescapedText calls behind isReady — these are imperative and fire
  // before translations register on first render, producing "Missing translation"
  // errors. Skeleton hides the output anyway while loading.
  const sections = isReady ? [{
    section: 'bundle',
    headingText: unescapedText('sideNav.sections.bundle'),
    items: [{
      navItem: 'suite',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.suite"
      }),
      to: {
        pathname: '/suite',
        search
      },
      onMouseEnter: () => scheduleHoverIntent('suite'),
      onMouseLeave: cancelHoverIntent
    }, {
      navItem: 'crm',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.crm"
      }),
      to: {
        pathname: '/crm',
        search
      },
      onMouseEnter: () => scheduleHoverIntent('crm'),
      onMouseLeave: cancelHoverIntent
    }, {
      navItem: 'bundle',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.bundle"
      }),
      to: {
        pathname: '/bundle',
        search
      },
      onMouseEnter: () => scheduleHoverIntent('bundle'),
      onMouseLeave: cancelHoverIntent
    }, {
      navItem: 'marketing-plus',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.marketing-plus"
      }),
      to: {
        pathname: '/marketing-plus',
        search
      },
      onMouseEnter: () => scheduleHoverIntent('marketing-plus'),
      onMouseLeave: cancelHoverIntent
    }]
  }, {
    section: 'core',
    headingText: unescapedText('sideNav.sections.core'),
    items: [{
      navItem: 'smart-crm',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.smart-crm"
      }),
      to: {
        pathname: '/smart-crm',
        search
      },
      onMouseEnter: () => scheduleHoverIntent('smart-crm'),
      onMouseLeave: cancelHoverIntent
    }, {
      navItem: 'marketing',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.marketing"
      }),
      to: {
        pathname: '/marketing',
        search
      },
      onMouseEnter: () => scheduleHoverIntent('marketing'),
      onMouseLeave: cancelHoverIntent
    }, {
      navItem: 'sales',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.sales"
      }),
      to: {
        pathname: '/sales',
        search
      },
      onMouseEnter: () => scheduleHoverIntent('sales'),
      onMouseLeave: cancelHoverIntent
    }, {
      navItem: 'service',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.service"
      }),
      to: {
        pathname: '/service',
        search
      },
      onMouseEnter: () => scheduleHoverIntent('service'),
      onMouseLeave: cancelHoverIntent
    }, {
      navItem: 'content',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.content"
      }),
      to: {
        pathname: '/content',
        search
      },
      onMouseEnter: () => scheduleHoverIntent('content'),
      onMouseLeave: cancelHoverIntent
    }, {
      navItem: 'data',
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.data"
      }),
      to: {
        pathname: '/data',
        search
      },
      onMouseEnter: () => scheduleHoverIntent('data'),
      onMouseLeave: cancelHoverIntent
    }, {
      navItem: 'commerce',
      title: isRevenueHubEnabled ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.revenue"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "sideNav.items.commerce"
      }),
      to: {
        pathname: '/commerce',
        search
      },
      onMouseEnter: () => scheduleHoverIntent('commerce'),
      onMouseLeave: cancelHoverIntent
    }]
  }] : [];

  // Derive selectedItemLabel from sections to avoid duplicating keys.
  const selectedItemLabel = (_sections$flatMap$fin = (_sections$flatMap$fin2 = sections.flatMap(s => s.items).find(item => item.navItem === selectedNavItem)) === null || _sections$flatMap$fin2 === void 0 ? void 0 : _sections$flatMap$fin2.title) !== null && _sections$flatMap$fin !== void 0 ? _sections$flatMap$fin : selectedNavItem;
  return (
    /*#__PURE__*/
    // 11 nav items × ~1 line each; widths approximate label lengths
    _jsx(Skeleton, {
      widths: ['60%', '40%', '55%', '65%', '50%', '45%', '55%', '45%', '50%', '40%', '55%'],
      heights: [56, 56, 56, 56, 56, 56, 56, 56, 56, 56, 56],
      children: /*#__PURE__*/_jsx(SharedSideNav, {
        selectedNavItem: selectedNavItem,
        selectedItemLabel: selectedItemLabel,
        menuAriaLabel: isReady ? unescapedText('sideNav.toggleMenu') : '',
        sections: sections,
        footerSlot: /*#__PURE__*/_jsx(PublicCurrencyPicker, {}),
        desktopPosition: "sticky",
        trackGlobalNavHeight: true
      })
    })
  );
};
export default PublicSideNav;